import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as LangIcon } from '@/assets/icons/ml-language-box.svg';
import { PageHeading, TeamHeading } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import { SortableTh, Table, TableContent, Td } from '@/components/Table';
import useSortState from '@/hooks/useSortState';
import { MLLanguageResource } from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';

const StyledTable = styled(Table)`
  td,
  th {
    vertical-align: middle;
    font-weight: 700;
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
  tbody tr {
    cursor: pointer;
    &:hover td {
      background: ${({ theme }) => theme.colors.grayscaleExtraLight};
    }
  }
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MLLanguages = () => {
  const { sort, setSort, sortQueryParamObj } = useSortState({
    property: 'pendingTranslationsCount',
    direction: SortDirection.DESC,
  });

  const navigate = useNavigate();

  const {
    data: languages,
    isError,
    isLoading,
  } = useQuery(
    [
      'ml_languages',
      {
        sort,
      },
    ],
    () =>
      api.get<never, MLLanguageResource[]>(`/v1/distribution/ml-languages`, {
        params: {
          ...sortQueryParamObj,
        },
      }),
    {
      staleTime: 90 * 1000,
    },
  );

  return (
    <>
      <TeamHeading />
      <PageHeading>Multi-language Translate & Voiceover</PageHeading>
      <ScrollableTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="language"
                title="Language"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="lastTranslationDate"
                title="Last Added"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="pendingTranslationsCount"
                title="Action Required"
              />
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!languages?.length}
              colSpan={3}
            >
              {languages?.map((lang) => (
                <tr key={lang.language} onClick={() => navigate(lang.language)}>
                  <Td>
                    <Title>
                      <LangIcon />
                      {lang.language}
                    </Title>
                  </Td>
                  <Td>
                    {DateTime.fromISO(
                      lang.lastTranslationDate,
                    ).toLocaleString()}
                  </Td>
                  <Td>{lang.pendingTranslationsCount}</Td>
                </tr>
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
    </>
  );
};

export default MLLanguages;
